// Why the API server refused a sign-in, when it says more than "wrong password": it is throttling
// failed sign-ins for this account here (429), or the account was deleted by the research team
// (403.account-deleted). lamp-core ignores HTTP status codes and turns every refused sign-in into
// the same error, so this reads those answers off window.fetch (wrapped once, in index.tsx) for the
// login screen and the ?a= sign-in link to report.

const TOO_MANY_FAILED_ATTEMPTS = "429.too-many-failed-attempts"
const ACCOUNT_DELETED = "403.account-deleted"
// The server lifts its block at most 15 minutes after the first of the failures.
const DEFAULT_RETRY_AFTER_MS = 15 * 60 * 1000

export type SignInRefusal = { reason: "throttled"; until: number } | { reason: "account-deleted" }

let refusal: SignInRefusal | null = null
let installed = false

/** Forgets what earlier requests said. Call right before each sign-in attempt. */
export function beginSignIn(): void {
  refusal = null
}

/** The throttling or deleted-account answer the server gave since beginSignIn(), if any. */
export function signInRefusal(): SignInRefusal | null {
  return refusal
}

/** The message for signInRefusal(), or null when the sign-in failed for another reason. */
export function signInRefusalMessage(t: (key: string, options?: any) => string): string | null {
  if (refusal?.reason === "throttled") {
    const minutes = Math.max(1, Math.ceil((refusal.until - Date.now()) / 60000))
    return t("Too many sign-in attempts. Please try again in about {{minutes}} minutes.", { minutes })
  }
  if (refusal?.reason === "account-deleted") {
    return t("This account has been deleted. Please contact your research team.")
  }
  return null
}

// Retry-After is in seconds (what lamp-server sends) or an HTTP date.
function retryAfterMs(value: string | null): number {
  const text = (value ?? "").trim()
  if (/^\d+$/.test(text)) return parseInt(text, 10) * 1000
  const date = Date.parse(text)
  return isNaN(date) ? DEFAULT_RETRY_AFTER_MS : Math.max(0, date - Date.now())
}

async function noteResponse(response: Response): Promise<void> {
  if (response?.status !== 429 && response?.status !== 403) return
  try {
    const body = JSON.parse(await response.clone().text())
    if (response.status === 429 && body?.error === TOO_MANY_FAILED_ATTEMPTS) {
      refusal = { reason: "throttled", until: Date.now() + retryAfterMs(response.headers.get("Retry-After")) }
    } else if (response.status === 403 && body?.error === ACCOUNT_DELETED) {
      refusal = { reason: "account-deleted" }
    }
  } catch {
    // Not JSON, or unreadable: nothing to note.
  }
}

/**
 * Wraps window.fetch (once) to note those answers. Every response passes through unchanged; the
 * wrapper reads a copy of 429 and 403 bodies before handing the response on, so the note is there
 * by the time lamp-core's sign-in fails.
 */
export function installSignInStatus(): void {
  if (installed || typeof window === "undefined" || typeof window.fetch !== "function") return
  installed = true
  const originalFetch = window.fetch
  window.fetch = function (...args: Parameters<typeof fetch>) {
    return originalFetch.apply(window, args).then(async (response: Response) => {
      await noteResponse(response)
      return response
    })
  }
}
